import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useDashboardStats } from './api/useDashboardStats';
import { useCalendar } from './api/useCalendar';
import { useActiveUsers } from './api/useActiveUsers';
import { InfoTip } from '@/components/shared';

interface Event {
  id: number;
  title: string;
  date: string;
  time: string;
  type: string;
}

interface ImportantLink {
  title: string;
  icon: string;
  href: string;
  color: string;
}

interface QuickAccessItem {
  title: string;
  description: string;
  icon: string;
  href: string;
  gradient: string;
}

interface DashboardStats {
  employee_count: number;
  metrics: {
    active_markets?: number;
    rollout_status?: string;
  };
  announcement?: {
    title?: string;
    body?: string;
  };
}

// Sample events data - would come from backend later
const upcomingEvents: Event[] = [
  { id: 1, title: 'Q1 All Hands Meeting', date: '2026-01-28', time: '10:00 AM EST', type: 'meeting' },
  { id: 2, title: 'Engineering Sprint Review', date: '2026-01-30', time: '2:00 PM EST', type: 'engineering' },
  { id: 3, title: 'Marketing Campaign Launch', date: '2026-02-03', time: '9:00 AM EST', type: 'marketing' },
  { id: 4, title: 'Design System Workshop', date: '2026-02-05', time: '11:00 AM EST', type: 'design' },
];

// Important links from the SharePoint site
const importantLinks: ImportantLink[] = [
  { title: 'Engineering & Technology', icon: '⚡', href: '/engineering', color: '#7c3aed' },
  { title: 'Social Spark Developer Page', icon: '💻', href: '/developers', color: '#3b82f6' },
  { title: 'GTM Rollout Hub', icon: '🚀', href: '/gtm', color: '#10b981' },
  { title: 'Resources', icon: '📚', href: '/resources', color: '#f59e0b' },
  { title: 'Brand Guidelines', icon: '🎨', href: '/brand', color: '#ec4899' },
  { title: 'Content Analytics Center', icon: '📊', href: '/analytics', color: '#14b8a6' },
];

// Quick access cards
const quickAccess: QuickAccessItem[] = [
  {
    title: 'Engineering & Technology',
    description: 'Central hub for engineering work, tools, documentation, and technical ownership.',
    icon: '🔧',
    href: '/engineering',
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)'
  },
  {
    title: 'Marketing',
    description: 'Campaign management, brand assets, and marketing resources.',
    icon: '📣',
    href: '/marketing',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)'
  },
  {
    title: 'Designs & Artwork',
    description: 'Creative assets, design files, and visual resources.',
    icon: '🎨',
    href: '/designs',
    gradient: 'linear-gradient(135deg, #f97316 0%, #c2410c 100%)'
  },
  {
    title: 'Documents',
    description: 'Shared documents, templates, and file storage.',
    icon: '📄',
    href: '/documents',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
  },
];

const getEventBadgeClasses = (type: string) => {
  const base = 'px-2 py-1 rounded-full text-xs font-semibold capitalize';
  switch (type) {
    case 'meeting': return `${base} bg-violet-500/20 text-violet-400`;
    case 'engineering': return `${base} bg-blue-500/20 text-blue-400`;
    case 'marketing': return `${base} bg-pink-500/20 text-pink-400`;
    case 'design': return `${base} bg-orange-500/20 text-orange-400`;
    default: return `${base} bg-gray-500/20 text-gray-400`;
  }
};


function Home(): React.ReactElement {
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const { data: dashboardStats, loading: statsLoading } = useDashboardStats();
  const { events: calendarEvents, loading: calendarLoading } = useCalendar();
  const { data: activeUsers } = useActiveUsers();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Only show events that occur within the next 30 days
  const eventsToShow: Event[] = (() => {
    const source = (calendarEvents && calendarEvents.length > 0) ? calendarEvents : [];
    const now = new Date();
    const max = new Date();
    max.setDate(now.getDate() + 30);
    return source.filter((e: Event) => {
      if (!e.date) return false;
      const d = new Date(e.date);
      return d >= now && d <= max;
    });
  })();

  const hasAnnouncement = !!(dashboardStats?.announcement?.title || dashboardStats?.announcement?.body);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <section className="relative flex items-center overflow-hidden py-6 lg:py-8">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-900 to-slate-900"></div>
          <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_25%_25%,rgba(124,58,237,0.3)_0%,transparent_50%),radial-gradient(circle_at_75%_75%,rgba(236,72,153,0.3)_0%,transparent_50%)]"></div>
        </div>
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-[1400px] mx-auto px-6">
          <div>
            <h1 className="font-display text-5xl lg:text-6xl font-extrabold text-white mb-3 leading-tight tracking-tight">
              Welcome to <span className="bg-gradient-to-r from-violet-500 to-pink-500 bg-clip-text text-transparent">SocialSpark</span>
            </h1>
            <p className="text-xl text-gray-300 mb-5 leading-relaxed max-w-[500px]">
              Your central hub for team collaboration, resources, and everything you need to spark innovation.
            </p>
            <div className="flex gap-4 flex-wrap">
              <Link to="/map" className="btn-primary px-8 py-3 text-base group">
                <span>Explore Our Locations</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="transition-transform duration-150 group-hover:translate-x-1">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5 relative">
            <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl px-6 py-3.5 text-center transition-all duration-300 hover:bg-white/15 hover:-translate-y-1">
              <div className="font-display text-[40px] font-bold text-white mb-0.5">{dashboardStats?.metrics?.active_markets ?? '—'}</div>
              <div className="text-sm font-semibold text-violet-200 uppercase tracking-widest drop-shadow-sm">
                Active Markets
                <InfoTip metric="home_active_markets" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl px-6 py-3.5 text-center transition-all duration-300 hover:bg-white/15 hover:-translate-y-1">
              <div className="font-display text-[40px] font-bold text-white mb-0.5">{dashboardStats?.employee_count ?? '—'}</div>
              <div className="text-sm font-semibold text-blue-200 uppercase tracking-widest drop-shadow-sm">
                Team Members
                <InfoTip metric="home_team_members" />
              </div>
            </div>
            <div className="col-span-2 bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl px-6 py-3.5 text-center transition-all duration-300 hover:bg-white/15 hover:-translate-y-1">
              <div className="font-display text-[40px] font-bold text-white mb-0.5">{dashboardStats?.metrics?.rollout_status ?? '—'}</div>
              <div className="text-sm font-semibold text-pink-200 uppercase tracking-widest drop-shadow-sm">
                Rollout Status
                <InfoTip metric="home_rollout_status" />
              </div>
            </div>
            <div className="col-span-2 bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl px-6 py-3.5 text-center transition-all duration-300 hover:bg-white/15 hover:-translate-y-1">
              <div className="flex items-center justify-center gap-2 mb-0.5">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                </span>
                <div className="font-display text-[40px] font-bold text-white leading-none">{activeUsers?.total ?? '—'}</div>
              </div>
              <div className="text-sm font-semibold text-emerald-200 uppercase tracking-widest drop-shadow-sm">
                Active Now
                <InfoTip metric="home_active_now" />
              </div>
              {activeUsers && (
                <div className="text-xs text-emerald-100/70 mt-1">
                  {activeUsers.consumer.toLocaleString()} consumer · {activeUsers.business.toLocaleString()} business
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-[1400px] mx-auto px-6 py-12">

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
          {/* Left Column - Announcement + Events */}
          <div className="space-y-8">
            {/* Announcement */}
            {hasAnnouncement && (
              <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.05),0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden flex">
                <div className="w-1.5 shrink-0 bg-gradient-to-b from-violet-500 to-pink-500" />
                <div className="px-6 py-5 flex-1 min-w-0">
                  <p className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-2">📢 Announcement</p>
                  {dashboardStats?.announcement?.title && (
                    <h3 className="font-semibold text-gray-900 text-base leading-snug">
                      {dashboardStats.announcement.title}
                    </h3>
                  )}
                  {dashboardStats?.announcement?.body && (
                    <p className="text-gray-500 text-sm leading-relaxed mt-1.5">
                      {dashboardStats.announcement.body}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Upcoming Events */}
            <div className="bg-white rounded-2xl p-8 shadow-[0_1px_3px_rgba(0,0,0,0.05),0_1px_2px_rgba(0,0,0,0.05)]">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-2xl font-bold text-gray-900">Upcoming Events</h2>
                <Link to="/events" className="text-sm text-violet-600 hover:text-violet-700 flex items-center gap-1 font-medium">
                  See all
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
              </div>
              <div className="space-y-3">
                {calendarLoading ? (
                  <div className="p-4 text-sm text-gray-500">Loading events…</div>
                ) : eventsToShow.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">No upcoming events in the next 30 days.</div>
                ) : (
                  eventsToShow.map((event) => (
                    <div key={event.id} className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex flex-col items-center justify-center text-white">
                        <span className="text-xs font-medium uppercase">{formatDate(event.date).split(' ')[0]}</span>
                        <span className="text-lg font-bold">{formatDate(event.date).split(' ')[1]}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900">{event.title}</h4>
                        <p className="text-sm text-gray-500">{event.time}</p>
                      </div>
                      <span className={getEventBadgeClasses(event.type)}>{event.type}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Referral programme — replaces the Bar Outreach Leaderboard (SSA-955).
                Outreach is tracked per referral code now, so this points at the
                dashboard that carries the numbers rather than duplicating them. */}
            <div className="bg-white rounded-2xl p-8 shadow-[0_1px_3px_rgba(0,0,0,0.05),0_1px_2px_rgba(0,0,0,0.05)]">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="font-display text-2xl font-bold text-gray-900">🎟️ Referral Codes</h2>
                  <p className="text-gray-500 text-sm mt-1.5 max-w-lg leading-relaxed">
                    Mint promoter and campaign codes, build the deal campaigns they attach to, and track
                    verified signups, dollars owed and redemptions per code.
                  </p>
                </div>
                <Link
                  to="/businesses?tab=referral-codes"
                  className="btn-primary px-5 py-2.5 text-sm group shrink-0"
                >
                  <span>Open dashboard</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="transition-transform duration-150 group-hover:translate-x-1">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <aside className="space-y-6">
            {/* Time Widget */}
            <div className="bg-white rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.05),0_1px_2px_rgba(0,0,0,0.05)]">
              <h3 className="font-display text-lg font-bold text-gray-900 mb-4">Office Hours</h3>
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-gray-50">
                  <div className="text-sm text-gray-500 mb-1">Hartford, CT</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {currentTime.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                      timeZone: 'America/New_York'
                    })}
                  </div>
                  <div className="text-sm text-gray-500">
                    {currentTime.toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'numeric',
                      day: 'numeric',
                      year: 'numeric',
                      timeZone: 'America/New_York'
                    })}
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-gray-50">
                  <div className="text-sm text-gray-500 mb-1">Tempe, AZ</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {currentTime.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                      timeZone: 'America/Phoenix'
                    })}
                  </div>
                  <div className="text-sm text-gray-500">
                    {currentTime.toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'numeric',
                      day: 'numeric',
                      year: 'numeric',
                      timeZone: 'America/Phoenix'
                    })}
                  </div>
                  <div className="text-xs text-amber-600 mt-1">2h behind</div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

export default Home;
